import { motion } from 'framer-motion'
import { TrendingUp, Users, DollarSign, Target, ArrowRight, CheckCircle2 } from 'lucide-react'
import { Link } from 'react-router-dom'

interface CaseStudy {
  id: string
  business: string
  industry: string
  challenge: string
  location: string
  timeline: string
  services: string[]
  results: {
    label: string
    value: string
    trend: 'up' | 'down'
    icon: typeof TrendingUp
  }[]
  strategy: string[]
  testimonial: {
    quote: string
    author: string
    role: string
  }
  color: string
}

const caseStudies: CaseStudy[] = [
  {
    id: 'local-bakery',
    business: 'Sweet Home Bakery',
    industry: 'Local Food & Beverage',
    challenge: 'A family-owned bakery struggling to compete with chain stores. They had minimal online presence and relied solely on walk-in customers.',
    location: 'Des Moines, IA',
    timeline: '6 months',
    services: ['Local SEO', 'Social Media Marketing', 'Google Ads'],
    results: [
      { label: 'Revenue Growth', value: '+187%', trend: 'up', icon: DollarSign },
      { label: 'Online Orders', value: '+340%', trend: 'up', icon: TrendingUp },
      { label: 'Instagram Followers', value: '+2,400', trend: 'up', icon: Users },
      { label: 'Cost Per Customer', value: '-42%', trend: 'down', icon: Target },
    ],
    strategy: [
      'Optimized Google My Business with mouthwatering photos and daily updates',
      'Created Instagram content strategy showcasing behind-the-scenes baking',
      'Launched targeted Google Ads for "fresh bakery near me" searches',
      'Implemented online ordering system with local delivery',
      'Built email list with loyalty rewards program',
    ],
    testimonial: {
      quote: "AdKarrot transformed our business. We went from barely surviving to having a waitlist for our signature items. Online orders now make up 60% of our revenue!",
      author: 'Maria Santos',
      role: 'Owner, Sweet Home Bakery',
    },
    color: 'carrot',
  },
  {
    id: 'fitness-studio',
    business: 'CorePower Fitness Studio',
    industry: 'Health & Wellness',
    challenge: 'A boutique fitness studio losing members to larger gyms with bigger marketing budgets. Needed to fill classes and build community.',
    location: 'Cedar Rapids, IA',
    timeline: '4 months',
    services: ['Facebook Ads', 'Content Marketing', 'Conversion Optimization'],
    results: [
      { label: 'New Members', value: '+156', trend: 'up', icon: Users },
      { label: 'Class Fill Rate', value: '+93%', trend: 'up', icon: TrendingUp },
      { label: 'Ad Spend ROI', value: '4.2x', trend: 'up', icon: DollarSign },
      { label: 'Lead Cost', value: '-58%', trend: 'down', icon: Target },
    ],
    strategy: [
      'Created hyper-targeted Facebook campaigns for local fitness enthusiasts',
      'Developed free workout video series to showcase instructor expertise',
      'Optimized landing pages with class schedules and easy booking',
      'Launched referral program incentivizing current members',
      'A/B tested messaging focusing on community over equipment',
    ],
    testimonial: {
      quote: "We were skeptical about digital marketing, but AdKarrot's data-driven approach proved us wrong. Our classes are now consistently full, and we're planning to expand!",
      author: 'Jake Morrison',
      role: 'Co-Founder, CorePower Fitness',
    },
    color: 'leaf',
  },
  {
    id: 'home-services',
    business: 'Premier Plumbing Solutions',
    industry: 'Home Services',
    challenge: 'Established plumbing company drowning in leads from directories but struggling with low-quality calls and poor conversion rates.',
    location: 'Iowa City, IA',
    timeline: '8 months',
    services: ['Local SEO', 'Google Ads', 'Website Redesign', 'Analytics'],
    results: [
      { label: 'Quality Leads', value: '+215%', trend: 'up', icon: Target },
      { label: 'Conversion Rate', value: '+128%', trend: 'up', icon: TrendingUp },
      { label: 'Average Job Value', value: '+$340', trend: 'up', icon: DollarSign },
      { label: 'Wasted Ad Spend', value: '-67%', trend: 'down', icon: DollarSign },
    ],
    strategy: [
      'Rebuilt website with emergency service CTAs and instant booking',
      'Implemented call tracking to identify highest-value lead sources',
      'Optimized Google Ads for high-intent keywords like "emergency plumber"',
      'Created service-specific landing pages with transparent pricing',
      'Set up automated review requests after completed jobs',
    ],
    testimonial: {
      quote: "AdKarrot didn't just get us more leads—they got us better leads. We're now booking jobs worth 40% more on average and spending less on advertising.",
      author: 'Tom Richardson',
      role: 'Owner, Premier Plumbing Solutions',
    },
    color: 'carrot',
  },
  {
    id: 'boutique-shop',
    business: 'Ember & Thread Boutique',
    industry: 'Retail Fashion',
    challenge: 'Small clothing boutique with beautiful inventory but struggling against online retailers. Foot traffic declining year over year.',
    location: 'Ames, IA',
    timeline: '5 months',
    services: ['Instagram Marketing', 'Email Marketing', 'E-commerce Strategy'],
    results: [
      { label: 'E-commerce Revenue', value: '+412%', trend: 'up', icon: DollarSign },
      { label: 'Email Revenue', value: '+$18K/mo', trend: 'up', icon: TrendingUp },
      { label: 'Instagram Engagement', value: '+890%', trend: 'up', icon: Users },
      { label: 'Return Customer Rate', value: '+145%', trend: 'up', icon: Target },
    ],
    strategy: [
      'Launched shoppable Instagram posts with style inspiration content',
      'Built email segmentation based on style preferences and purchase history',
      'Created "Shop the Look" campaigns featuring local influencers',
      'Implemented cart abandonment sequences with personalized incentives',
      'Hosted virtual shopping events with exclusive online-only deals',
    ],
    testimonial: {
      quote: "I thought my boutique would be another casualty of online shopping. Instead, AdKarrot helped us thrive by meeting our customers where they are—online.",
      author: 'Sarah Chen',
      role: 'Owner, Ember & Thread',
    },
    color: 'leaf',
  },
]

export default function CaseStudies() {
  return (
    <>
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-white via-cloud-50/30 to-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-block mb-6 px-5 py-2.5 bg-gradient-to-r from-carrot-50 to-carrot-100/50 text-carrot-700 rounded-full text-sm font-semibold tracking-wide shadow-subtle">
              Real Results for Real Businesses
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-ink-900 mb-8 leading-[1.1]">
              Small Business Success Stories
            </h1>
            <p className="text-xl md:text-2xl text-ink-600 font-light leading-relaxed">
              See how we've helped Iowa small businesses grow revenue, reduce costs, and dominate their local markets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="space-y-32">
            {caseStudies.map((study, idx) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.8, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                {/* Case Study Header */}
                <div className="grid md:grid-cols-3 gap-8 mb-12">
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-4 py-1.5 bg-${study.color}-100 text-${study.color}-700 rounded-full text-sm font-semibold`}>
                        {study.industry}
                      </span>
                      <span className="text-ink-500 text-sm">•</span>
                      <span className="text-ink-500 text-sm">{study.location}</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-ink-900 mb-4 tracking-tight">
                      {study.business}
                    </h2>
                    <p className="text-xl text-ink-600 leading-relaxed font-light">
                      {study.challenge}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm font-semibold text-ink-500 mb-1">Timeline</div>
                      <div className="text-lg font-bold text-ink-900">{study.timeline}</div>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-ink-500 mb-2">Services</div>
                      <div className="flex flex-wrap gap-2">
                        {study.services.map((service) => (
                          <span
                            key={service}
                            className="px-3 py-1 bg-ink-100 text-ink-700 rounded-full text-xs font-medium"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {study.results.map((result, resultIdx) => {
                    const Icon = result.icon
                    return (
                      <motion.div
                        key={resultIdx}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: resultIdx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                        className="bg-gradient-to-br from-white to-cloud-50 rounded-3xl border border-ink-100 p-6 shadow-card hover:shadow-soft transition-all duration-500 ease-apple"
                      >
                        <div className={`w-12 h-12 rounded-2xl bg-${study.color}-100 flex items-center justify-center mb-4`}>
                          <Icon className={`w-6 h-6 text-${study.color}-600`} />
                        </div>
                        <div className={`text-4xl font-bold mb-2 ${result.trend === 'up' ? 'text-leaf-600' : 'text-carrot-600'}`}>
                          {result.value}
                        </div>
                        <div className="text-sm text-ink-600 font-medium">{result.label}</div>
                      </motion.div>
                    )
                  })}
                </div>

                {/* Strategy */}
                <div className="grid md:grid-cols-2 gap-12 mb-12">
                  <div>
                    <h3 className="text-2xl font-bold text-ink-900 mb-6">Our Strategy</h3>
                    <div className="space-y-4">
                      {study.strategy.map((item, itemIdx) => (
                        <motion.div
                          key={itemIdx}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: itemIdx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                          className="flex gap-3"
                        >
                          <CheckCircle2 className={`w-6 h-6 text-${study.color}-600 flex-shrink-0 mt-0.5`} />
                          <p className="text-ink-700 leading-relaxed">{item}</p>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  {/* Testimonial */}
                  <div className={`bg-gradient-to-br from-${study.color}-50 to-white rounded-3xl p-8 md:p-10 border border-${study.color}-100 shadow-soft`}>
                    <div className="text-5xl text-carrot-400 mb-4 leading-none">"</div>
                    <p className="text-xl text-ink-800 mb-6 leading-relaxed font-light italic">
                      {study.testimonial.quote}
                    </p>
                    <div className="border-t border-ink-200 pt-4">
                      <div className="font-bold text-ink-900">{study.testimonial.author}</div>
                      <div className="text-sm text-ink-600">{study.testimonial.role}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-carrot-500 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-leaf-500 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
                Ready to write your success story?
              </h2>
              <p className="text-xl md:text-2xl text-cloud-200 mb-12 leading-relaxed font-light max-w-3xl mx-auto">
                Join these thriving Iowa businesses. Book a free strategy call and let's create your custom growth roadmap.
              </p>
              <Link
                to="/pricing"
                className="group inline-flex items-center px-10 py-5 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold text-lg rounded-2xl shadow-glow hover:shadow-card-hover transition-all duration-300 ease-apple transform hover:scale-[1.02]"
              >
                Book Your Strategy Call
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-0.5 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
